import { useState } from 'react';
import { Loader2, Send, StickyNote } from 'lucide-react';
import { useCreateNote, type NoteCategory } from '../hooks/useApprenantNotes.js';

const CATEGORIES: { value: NoteCategory; label: string; color: string }[] = [
  { value: 'general', label: 'Général', color: 'bg-neutral-100 text-neutral-700' },
  { value: 'commercial', label: 'Commercial', color: 'bg-sky-100 text-sky-700' },
  { value: 'pedagogique', label: 'Pédagogique', color: 'bg-violet-100 text-violet-700' },
  { value: 'admin', label: 'Admin', color: 'bg-amber-100 text-amber-700' },
  { value: 'alerte', label: 'Alerte', color: 'bg-rose-100 text-rose-700' },
];

interface Props {
  apprenantId: string;
  compact?: boolean;
}

/**
 * Composant dédié — formulaire d'ajout d'une note. Réutilisé par NotesPanel
 * (fiche apprenant) et par la page Communications (filtré par apprenantId).
 */
export function AddNoteForm({ apprenantId, compact = false }: Props) {
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<NoteCategory>('general');
  const createMut = useCreateNote(apprenantId);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!content.trim() || createMut.isPending) return;
    try {
      await createMut.mutateAsync({ content: content.trim(), category });
      setContent('');
      setCategory('general');
    } catch (err) {
      // erreur affichée via createMut.error
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <div className="flex items-start gap-2">
        <StickyNote className="mt-2 h-4 w-4 flex-shrink-0 text-amber-500" />
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Ajouter une note interne… (max 8000 caractères)"
          rows={compact ? 2 : 3}
          maxLength={8000}
          className="focus:border-primary-400 focus:ring-primary-100 flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2"
        />
      </div>
      <div className="flex items-center justify-between gap-2 pl-6">
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-[10px] uppercase tracking-wider text-neutral-500">Type :</span>
          {CATEGORIES.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => setCategory(c.value)}
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold transition-all ${
                category === c.value
                  ? c.color + ' ring-2 ring-current ring-offset-1'
                  : 'bg-neutral-50 text-neutral-500 hover:bg-neutral-100'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <button
          type="submit"
          disabled={!content.trim() || createMut.isPending}
          className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
        >
          {createMut.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
          Enregistrer
        </button>
      </div>
      {createMut.error && (
        <div className="rounded border border-rose-200 bg-rose-50 px-3 py-1.5 text-[11px] text-rose-700">
          {(createMut.error as Error).message}
        </div>
      )}
    </form>
  );
}
